import React, { useId } from 'react';
interface WorkiumLogoProps {
  variant?: 'default' | 'light' | 'dark' | 'icon';
  className?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
}
export function WorkiumLogo({
  variant = 'default',
  className = '',
  size = 'md'
}: WorkiumLogoProps) {
  const sizes = {
    sm: 'h-6',
    md: 'h-8',
    lg: 'h-12',
    xl: 'h-16'
  };
  const iconSizes = {
    sm: 'w-6 h-6',
    md: 'w-8 h-8',
    lg: 'w-12 h-12',
    xl: 'w-16 h-16'
  };
  const textColors = {
    default: 'text-white',
    light: 'text-white',
    dark: 'text-slate-950',
    icon: ''
  };
  const isIconOnly = variant === 'icon';
  const isDarkBg = variant === 'default' || variant === 'light';
  // Unique gradient IDs per instance to avoid SVG conflicts
  const uid = useId().replace(/:/g, '');
  return <div className={`flex items-center gap-2.5 ${className}`} data-id="element-772">
      {/* Logo Mark */}
      <div className={`relative flex items-center justify-center shrink-0 ${iconSizes[size]}`} data-id="element-773">
        {isDarkBg && <div className="absolute inset-[-6px] bg-indigo-400/10 rounded-full blur-xl pointer-events-none" data-id="element-774" />}
        <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full relative" data-id="element-775">
          <defs data-id="element-776">
            <linearGradient id={`${uid}-core`} x1="14" y1="14" x2="26" y2="26" gradientUnits="userSpaceOnUse" data-id="element-777">
              <stop stopColor="#A5B4FC" data-id="element-778" />
              <stop offset="1" stopColor="#6366F1" data-id="element-779" />
            </linearGradient>
            <linearGradient id={`${uid}-ring`} x1="4" y1="4" x2="36" y2="36" gradientUnits="userSpaceOnUse" data-id="element-780">
              <stop stopColor="#818CF8" stopOpacity="0.6" data-id="element-781" />
              <stop offset="0.5" stopColor="#60A5FA" stopOpacity="0.3" data-id="element-782" />
              <stop offset="1" stopColor="#A78BFA" stopOpacity="0.6" data-id="element-783" />
            </linearGradient>
          </defs>

          {/* Outer orbit ring — clean, subtle */}
          <circle cx="20" cy="20" r="16" stroke={`url(#${uid}-ring)`} strokeWidth="1.2" className="origin-center animate-[spin_30s_linear_infinite]" data-id="element-784" />

          {/* Connecting lines — from center to each satellite */}
          <line x1="20" y1="20" x2="20" y2="6" stroke="#818CF8" strokeWidth="1.5" strokeOpacity="0.5" strokeLinecap="round" data-id="element-785" />

          <line x1="20" y1="20" x2="32.1" y2="27" stroke="#60A5FA" strokeWidth="1.5" strokeOpacity="0.5" strokeLinecap="round" data-id="element-786" />

          <line x1="20" y1="20" x2="7.9" y2="27" stroke="#A78BFA" strokeWidth="1.5" strokeOpacity="0.5" strokeLinecap="round" data-id="element-787" />

          {/* Central hub — larger, gradient filled */}
          <circle cx="20" cy="20" r="5.5" fill={`url(#${uid}-core)`} data-id="element-788" />
          {/* Inner bright core for depth */}
          <circle cx="20" cy="20" r="2" fill="white" fillOpacity="0.3" data-id="element-789" />

          {/* Satellite nodes — equidistant on the orbit ring, consistent size */}
          <circle cx="20" cy="6" r="3" fill="#A5B4FC" data-id="element-790" />
          <circle cx="32.1" cy="27" r="3" fill="#7DD3FC" data-id="element-791" />
          <circle cx="7.9" cy="27" r="3" fill="#C4B5FD" data-id="element-792" />
        </svg>
      </div>

      {/* Wordmark */}
      {!isIconOnly && <div className={`flex items-center ${sizes[size]} ${textColors[variant]}`} data-id="element-793">
          <span className="font-extrabold tracking-[-0.02em]" style={{
        fontSize: size === 'sm' ? '1.125rem' : size === 'md' ? '1.375rem' : size === 'lg' ? '2rem' : '2.75rem'
      }} data-id="element-794">
            Workium AI
          </span>
          <span className="font-extrabold text-indigo-400" style={{
        fontSize: size === 'sm' ? '1.125rem' : size === 'md' ? '1.375rem' : size === 'lg' ? '2rem' : '2.75rem'
      }} data-id="element-795">
            .
          </span>
        </div>}
    </div>;
}